/**
 * 현재 주소가 어느 메뉴에 속하는지 판정한다.
 *
 * 🔴 왜 NavLink 의 isActive 를 안 쓰나
 *   React Router 의 NavLink 는 기본적으로 하위 경로까지 활성으로 본다.
 *   그래서 /trading 탭이 /trading/account 에서도 켜져 두 탭이 동시에 활성이 됐다.
 *
 *   end 를 붙이면 정확히 일치할 때만 켜지는데, 이번에는 추천 상세(/trading/123)에서
 *   아무 탭도 안 켜진다. 어느 쪽도 맞지 않는다.
 *
 * 그래서 백엔드와 같은 규칙을 쓴다 - 가장 긴 경로가 이긴다.
 *
 *   /trading/account/orders  →  /trading/account   (/trading 이 아니다)
 *   /trading/123             →  /trading
 *   /                        →  없음 (홈에서는 아무 탭도 안 켜진다)
 *
 * 백엔드 MenuCatalog.resolveByApiPath 가 api_prefix 로 하는 것과 같은 판정이다.
 * 규칙이 둘로 갈리면 화면에서 켜진 탭과 실제 권한 판정이 어긋난다.
 */

/** 이 판정에 필요한 것만. Menu 전체를 받지 않아 테스트가 가벼워진다. */
interface PathOwner {
  code: string;
  path: string;
}

/**
 * 활성 메뉴 코드. 어느 메뉴에도 속하지 않으면 null.
 *
 * @param pathname 현재 주소. location.pathname 을 그대로 넣는다
 */
export function resolveActiveMenuCode(
  pathname: string,
  menus: readonly PathOwner[],
): string | null {
  const normalized = stripTrailingSlash(pathname);

  return (
    menus
      .filter((menu) => covers(stripTrailingSlash(menu.path), normalized))
      // 긴 경로가 먼저 오게 정렬한 뒤 첫 번째를 집는다.
      .sort((a, b) => b.path.length - a.path.length)[0]?.code ?? null
  );
}

/**
 * 주어진 주소가 이 메뉴의 영역에 속하는지.
 *
 * 단순 startsWith 로는 부족하다. /trading 이 /trading-foo 까지 삼킨다.
 * 접두사 뒤가 끝이거나 / 여야 같은 영역으로 본다.
 * 백엔드 Menu.covers 와 같은 규칙이다.
 */
function covers(menuPath: string, pathname: string): boolean {
  if (!pathname.startsWith(menuPath)) {
    return false;
  }
  return pathname.length === menuPath.length || pathname[menuPath.length] === '/';
}

/**
 * 끝의 / 를 떼어낸다.
 *
 * /trading/ 과 /trading 은 같은 화면인데 문자열로는 다르다.
 * 루트(/)는 그대로 둔다 - 떼면 빈 문자열이 되어 모든 경로에 걸린다.
 */
function stripTrailingSlash(path: string): string {
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}
